/**
 * The refund flow's money rows (BON-147) — shared by the drawer's pre-submit
 * breakdown, the receipt panel that replaces it, and the Payment card's
 * settled-refund row (BON-335 round 4), which is why they live apart from
 * all three.
 *
 * `moneyBreakdown` is the load-bearing one. It renders two different axes
 * (ADR-0019) and they do NOT always agree: the receipt is what the booking was
 * contracted for, `refundCents` is the net cash actually recorded against it,
 * and part-payments, comps and import gaps make the second smaller. The server
 * refunds the second, so that is what the total line says — quoting the receipt
 * here would promise a CSR an amount we are not going to mint.
 */
import type { BookingMoney, RefundMethod } from '../../api/bookingsV2';

import { formatCents, wasServiceFeeCharged } from './bookingDetailModel';

/** How each destination is named in prose. Kept in one place so the drawer's
 *  receipt and the Payment card's refund row describe the same method the
 *  same way. */
export function refundMethodLabel(method: RefundMethod): string {
  if (method === 'GIFT_CARD') return 'a Bonsai Bar gift card';
  if (method === 'GIFT_PASS') return 'gift passes';
  if (method === 'CARD') return 'the original card';
  return 'another payment method';
}

export function moneyRow(label: string, value: string, strong = false) {
  return (
    <div className="flex items-center justify-between py-1">
      <span
        className={
          strong
            ? 'text-sm font-semibold text-bark-900 dark:text-stone-100'
            : 'text-sm text-stone-500 dark:text-stone-400'
        }
      >
        {label}
      </span>
      <span
        className={
          strong
            ? 'text-sm font-bold text-bark-900 dark:text-stone-100'
            : 'text-sm text-bark-900 dark:text-stone-100'
        }
      >
        {value}
      </span>
    </div>
  );
}

export function moneyBreakdown(money: BookingMoney, refundCents: number) {
  const differs = refundCents !== money.totalCents;
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 dark:border-stone-700 dark:bg-[#1a201d]">
      {moneyRow('Tickets', formatCents(money.subtotalCents))}
      {moneyRow('Tax', formatCents(money.taxesCents))}
      {wasServiceFeeCharged(money.feesCents) &&
        moneyRow('Service fee', formatCents(money.feesCents))}
      {/* Only when the two axes diverge. A breakdown whose parts do not sum to
          its total reads as a bug unless the difference is named. */}
      {differs && moneyRow('Booking total', formatCents(money.totalCents))}
      {moneyRow('Total refund', formatCents(refundCents), true)}
    </div>
  );
}
